import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import useI18n from '@/hooks/useI18n';
import { UserContext } from '@/context/UserContext';
import { BrandMemberRole } from '@/types/enums';
import { Button } from '../common/Button';
import { NumberOne, NumberTwo, NumberThree, PlusIcon } from '@/svg';

const BADGE_FILL = '#FD6917';

interface StepCardProps {
  badge: React.ReactNode;
  title: string;
  description: string;
}

const StepCard: React.FC<StepCardProps> = ({ badge, title, description }) => (
  <div className="flex min-w-0 flex-1 flex-col gap-2 rounded-3xl border border-[#E5E7EB] bg-white p-5">
    {badge}
    <p className="font-content text-base font-extrabold leading-5 tracking-[0.16px] text-[#111827]">
      {title}
    </p>
    <p className="font-content text-base font-normal leading-5 tracking-[0.16px] text-[#6B7280]">
      {description}
    </p>
  </div>
);

/**
 * Dashboard state for a brand that has no campaign yet. Replaces the previous
 * CreateCampaign + BlankDashboard pair, which stacked two competing heroes.
 */
const BrandNoCampaign: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const user = useContext(UserContext)?.user;

  // Brand members on the `user` role cannot create campaigns.
  const canCreateCampaign = user?.brandMemberRole !== BrandMemberRole.User;

  const steps = [
    {
      badge: <NumberOne fill={BADGE_FILL} textColor="#FFFFFF" />,
      title: t('common.theyShop'),
      description: t(
        'common.customers_purchase_in_store_or_online_driving_instant_revenue',
      ),
    },
    {
      badge: <NumberTwo fill={BADGE_FILL} textColor="#FFFFFF" />,
      title: t('common.theyPost'),
      description: t(
        'common.they_share_authentic_Instagram_and_Tiktok_content_amplifying_your_brands_reach',
      ),
    },
    {
      badge: <NumberThree fill={BADGE_FILL} textColor="#FFFFFF" />,
      title: t('common.theyEarn'),
      description: t(
        'common.reward_their_social_impact_with_cashback_turning_every_sale_into_a_marketing_win',
      ),
    },
  ];

  return (
    <div
      data-testid="brand-no-campaign"
      className="flex flex-col items-center px-6"
    >
      <div className="flex w-full flex-col gap-4 pb-4 pt-2 lg:flex-row lg:items-center lg:justify-between lg:gap-5">
        <div className="flex flex-col justify-center">
          <p className="font-header text-base font-semibold capitalize leading-6 tracking-[0.096px] text-[#172B4D]">
            {t('common.campaign')}
          </p>
          <p className="font-header text-sm leading-6 tracking-[0.084px] text-[#9B919D]">
            {t('common.createYourCampaignToSeeTheResponse')}
          </p>
        </div>
        {canCreateCampaign ? (
          <Button
            icon={<PlusIcon stroke="#FFFFFF" />}
            iconPosition="start"
            onClick={() => navigate('/create-campaign')}
            className="!bg-[#231254] hover:!bg-[#1B0E42] active:!bg-[#160B36] max-lg:w-full"
          >
            {t('common.createNewCampaign')}
          </Button>
        ) : (
          <p className="font-header text-sm leading-6 text-[#9B919D]">
            {t('common.askBrandAdminToLaunchCampaign')}
          </p>
        )}
      </div>

      <div className="flex w-full flex-col gap-4 rounded-3xl border border-[#E5E7EB] bg-[#F8F8F8] p-5 lg:p-8">
        <div className="flex flex-col items-center justify-center text-center tracking-[-0.64px] text-[#231254]">
          <span className="font-header text-2xl leading-9 lg:text-[32px] lg:leading-[54px]">
            {t('common.yourNextBigMarketingWinStartsHere')}
          </span>
          <span className="font-header text-base leading-8">
            {t('common.letsGetYourBrandInFrontOfMillions')}
          </span>
        </div>
        <div className="flex w-full flex-col gap-6 md:flex-row md:items-stretch">
          {steps.map((step) => (
            <StepCard
              key={step.title}
              badge={step.badge}
              title={step.title}
              description={step.description}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default BrandNoCampaign;
